import React, { useEffect, useMemo, useState } from 'react';
import BookingButton from './BookingButton';
import ServiceGallery from './ServiceGallery';
import ImageLightbox from './ImageLightbox';
import { IconPanel } from './ServiceIcon';

const ServiceModal = ({ service, onClose }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Serviços vindos do catálogo já trazem a galeria resolvida; os que só têm
  // uma imagem continuam a funcionar sem alterações.
  const media = useMemo(() => {
    if (Array.isArray(service?.media) && service.media.length) return service.media;
    return service?.image
      ? [{ type: 'photo', src: service.image, alt: service.title }]
      : [];
  }, [service]);

  // Fechar modal com ESC (quando o visualizador está aberto, o ESC é dele)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && lightboxIndex === null) onClose();
    };

    document.addEventListener('keydown', handleEsc);
    // Prevenir rolagem do body quando o modal está aberto
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, lightboxIndex]);

  // Prevenir propagação de cliques no conteúdo do modal
  const handleModalContentClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto relative animate-scaleIn"
        onClick={handleModalContentClick}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-[#3b473c]/70 backdrop-blur-sm text-white border border-white/25 hover:bg-[#5c7160] transition-colors z-20"
          aria-label="Fechar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Galeria de fotografias do serviço */}
          {media.length ? (
            <div className="relative h-72 sm:h-80 md:h-auto md:min-h-[28rem] bg-[#ece5d8]">
              <ServiceGallery
                media={media}
                title={service.title}
                variant="modal"
                onExpand={(index) => setLightboxIndex(index)}
                className="md:absolute md:inset-0"
              />
            </div>
          ) : (
            // Sem fotografia própria: o mesmo painel com ícone dos cartões.
            <IconPanel name={service.icon} className="h-44 sm:h-56 md:h-auto md:min-h-[24rem]" />
          )}

          {/* Content section */}
          <div className="p-6 pt-10 md:p-8 flex flex-col h-full md:justify-center">
            <h2 className="text-2xl font-light text-[#5c7160] mb-2 pr-5">{service.title}</h2>

            {service.lengthPrices ? (
              // Preço por comprimento de cabelo: quatro mosaicos em vez de um só valor.
              <div className="mb-5">
                <p className="text-xs uppercase tracking-[0.14em] text-[#5c7160]/60 mb-2">
                  Preço por comprimento de cabelo
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {service.lengthPrices.map(({ key, label, value, numeric }) => (
                    <div key={key} className="rounded-xl bg-[#F5F1E9] px-3 py-2">
                      <span className="block text-[11px] text-[#5c7160]/70">{label}</span>
                      <span
                        className={
                          numeric
                            ? 'font-medium text-[#c0a080] tabular-nums'
                            : 'text-sm italic font-light text-[#5c7160]/60'
                        }
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                {service.duration && (
                  <p className="mt-2 text-sm text-[#5c7160]/70">{service.duration}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="px-3 py-1 bg-[#F5F1E9] rounded-full text-[#c0a080] font-medium">
                  {service.priceSummary?.prefix && (
                    <span className="mr-1 text-sm font-light text-[#5c7160]/70">
                      {service.priceSummary.prefix}
                    </span>
                  )}
                  {service.priceSummary?.value}
                </div>
                {service.duration && (
                  <div className="px-3 py-1 bg-[#F5F1E9] rounded-full text-[#5c7160]">
                    {service.duration}
                  </div>
                )}
              </div>
            )}

            <div className="mt-2">
              {service.details?.length > 0 && (
                <>
                  <h3 className="text-lg font-medium text-[#5c7160] mb-3">Detalhes do serviço</h3>
                  <ul className="space-y-3">
                    {service.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start text-[#5c7160]/80">
                        <span className="text-[#a5bf99] mr-2 text-xl">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Booking button */}
            <div className="pt-4">
              <div className="w-full">
                <BookingButton fullWidth variant="solid">
                  <span className="flex items-center justify-center w-full">
                    <span className="mr-2">Agendar Agora</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </span>
                </BookingButton>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          media={media}
          title={service.title}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default ServiceModal;
